import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconPhoneCall,
  IconTransfer,
  IconMessage,
  IconPhoneOff,
  IconUsers,
  IconBan,
  IconPhoneX,
  IconUserMinus,
  IconCalendarEvent,
  IconHelpCircle,
} from '@tabler/icons-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@core-ui/Components/ui/tabs';
import type { SessionStats } from '../types/dashboard.types';
import { Flame } from 'lucide-react';

type StatFilterType = 'action' | 'disposition';

interface StatsCardsProps {
  stats: SessionStats;
  onClickStat: (type: string, filter: string) => void;
  fuegoLeadCount?: number | null;
  /**
   * The filters currently applied to the calls table. A card whose filter value
   * matches renders as selected, so the cards and the table's dropdowns can
   * never disagree about which filter is on.
   */
  dispositionFilter: string;
  actionFilter: string;
  /**
   * Which tab is open. Controlled from the page ON PURPOSE: clicking a card
   * refetches, the page swaps itself for the loading skeleton, and this
   * component unmounts — an uncontrolled `defaultValue` would come back on
   * "outcomes" every time, throwing you out of the tab you were filtering from.
   */
  activeTab: string;
  onTabChange: (tab: string) => void;
}

/**
 * What a card filters the calls table by. The value is the stable
 * disposition/action id (NOT the translated label). Cards that aren't a single
 * filterable value (e.g. the composite "fuego leads" or the aggregate
 * "attempts" cards) carry no filter and render as plain figures.
 */
interface StatCardFilter {
  type: StatFilterType;
  value: string;
  isSelected: boolean;
}

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  iconBgColor: string;
  valueColor?: string;
  onSelected: (type: string, filter: string) => void;
  filter?: StatCardFilter;
  qaId?: string;
}

const BASE_CARD_CLASS = 'bg-muted/40 rounded-xl border p-4 min-h-[120px] text-left transition';
const SELECTED_CARD_CLASS = 'border-primary bg-primary/10 ring-1 ring-primary/30';
const CLICKABLE_CARD_CLASS = 'border-border cursor-pointer hover:bg-muted';
const STATIC_CARD_CLASS = 'border-border cursor-default';

function cardStateClass (isSelected: boolean, isClickable: boolean): string {
  if (isSelected) {
    return SELECTED_CARD_CLASS;
  }
  return isClickable ? CLICKABLE_CARD_CLASS : STATIC_CARD_CLASS;
}

function StatCard ({ label, value, icon, iconBgColor, valueColor = 'text-foreground', onSelected, filter, qaId }: StatCardProps): React.JSX.Element {
  const isSelected = filter?.isSelected ?? false;
  // A selected card stays clickable even when its count is 0, so the filter it
  // applied can always be cleared from the card itself.
  const isClickable = filter !== undefined && (value !== 0 || isSelected);

  const handleClick = (): void => {
    if (filter === undefined || !isClickable) {
      return;
    }
    // Clicking the selected card toggles its filter off.
    if (isSelected) {
      onSelected(filter.type, 'all');
      return;
    }
    // Cards and dropdowns narrow the same list, so a card replaces whichever
    // other card filter was on rather than stacking on top of it.
    if (filter.type === 'action') {
      onSelected('action', filter.value);
      onSelected('disposition', 'all');
    } else {
      onSelected('disposition', filter.value);
      onSelected('action', 'all');
    }
  };

  return (
    <button
      type="button"
      disabled={!isClickable}
      aria-pressed={filter !== undefined ? isSelected : undefined}
      data-qa-id={qaId ?? (filter !== undefined ? `stat-card-${filter.value}` : undefined)}
      className={`${BASE_CARD_CLASS} ${cardStateClass(isSelected, isClickable)}`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`p-2 rounded-lg ${iconBgColor}`}>
          {icon}
        </div>
      </div>
      <div>
        <p className={`text-3xl font-bold ${valueColor}`}>{value}</p>
      </div>
    </button>
  );
}

export default function StatsCards ({ stats, onClickStat, fuegoLeadCount, dispositionFilter, actionFilter, activeTab, onTabChange }: StatsCardsProps): React.JSX.Element {
  const { t } = useTranslation();

  const filterBy = (type: StatFilterType, value: string): StatCardFilter => ({
    type,
    value,
    isSelected: (type === 'action' ? actionFilter : dispositionFilter) === value,
  });

  return (
    <Tabs value={activeTab} onValueChange={onTabChange} className="bg-card rounded-xl border border-border p-4">
      <TabsList className="mb-4">
        <TabsTrigger value="outcomes">{t('dashboard.statsCards.outcomes', 'Outcomes')}</TabsTrigger>
        <TabsTrigger value="dispositions">{t('dashboard.statsCards.dispositions', 'Dispositions')}</TabsTrigger>
        <TabsTrigger value="attempts">{t('dashboard.statsCards.attempts', 'Attempts')}</TabsTrigger>
      </TabsList>

      {/* Outcomes */}
      <TabsContent value="outcomes">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {/*
            "Connected" = calls that ended in a real conversation, taken from
            the dispositions. This card used to read `action="answered"`, which
            is written for every call that reaches the leg (voicemail pickups
            and wrong numbers included) and showed 37 on a 36-call session.

            Clicking it filters the calls table to the rows whose Action is
            Answered — the same `action=answered` query the Action dropdown
            sends. Because the count above comes from dispositions while the
            filter is by action, the filtered list can hold more rows than the
            number on the card.
          */}
          <StatCard
            label={t('dashboard.statsCards.connected', 'Connected')}
            filter={filterBy('action', 'answered')}
            value={stats.connected}
            icon={<IconPhoneCall className="h-5 w-5 text-green-500" />}
            iconBgColor="bg-green-500/10"
            valueColor="text-green-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.transferred')}
            filter={filterBy('action', 'transferred')}
            value={stats.transferred}
            icon={<IconTransfer className="h-5 w-5 text-blue-500" />}
            iconBgColor="bg-blue-500/10"
            valueColor="text-blue-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.setAppointments')}
            filter={filterBy('disposition', 'set_appointment')}
            value={stats.setAppointments}
            icon={<IconCalendarEvent className="h-5 w-5 text-purple-500" />}
            iconBgColor="bg-purple-500/10"
            valueColor="text-purple-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.leftVoicemail')}
            filter={filterBy('disposition', 'left_vm')}
            value={stats.leftVoicemail}
            icon={<IconMessage className="h-5 w-5 text-orange-500" />}
            iconBgColor="bg-orange-500/10"
            valueColor="text-orange-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.noAnswer')}
            filter={filterBy('disposition', 'no_answer')}
            value={stats.noAnswer}
            icon={<IconPhoneOff className="h-5 w-5 text-red-500" />}
            iconBgColor="bg-red-500/10"
            valueColor="text-red-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.wrongNumber')}
            filter={filterBy('disposition', 'wrong_number')}
            value={stats.wrongNumber}
            icon={<IconPhoneX className="h-5 w-5 text-gray-400" />}
            iconBgColor="bg-gray-500/10"
            onSelected={onClickStat}
          />
        </div>
      </TabsContent>

      {/* Dispositions */}
      <TabsContent value="dispositions">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard
            label={t('dashboard.statsCards.gkConversations')}
            filter={filterBy('disposition', 'gatekeeper_conversation')}
            value={stats.gatekeeperConversation}
            icon={<IconUsers className="h-5 w-5 text-cyan-500" />}
            iconBgColor="bg-cyan-500/10"
            valueColor="text-cyan-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.decisionMaker', 'Decision Maker')}
            filter={filterBy('disposition', 'decision_maker_conversation')}
            value={stats.decisionMakerConversation}
            icon={<IconUsers className="h-5 w-5 text-purple-500" />}
            iconBgColor="bg-purple-500/10"
            valueColor="text-purple-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.referred')}
            filter={filterBy('disposition', 'referred_another_contact')}
            value={stats.referredAnotherContact}
            icon={<IconUsers className="h-5 w-5 text-indigo-500" />}
            iconBgColor="bg-indigo-500/10"
            valueColor="text-indigo-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.noLongerWithCompany')}
            filter={filterBy('disposition', 'no_longer_with_company')}
            value={stats.noLongerWithCompany}
            icon={<IconUserMinus className="h-5 w-5 text-pink-500" />}
            iconBgColor="bg-pink-500/10"
            valueColor="text-pink-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.optOut')}
            filter={filterBy('disposition', 'opt_out')}
            value={stats.optOut}
            icon={<IconBan className="h-5 w-5 text-red-400" />}
            iconBgColor="bg-red-500/10"
            valueColor="text-red-400"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.phoneTree')}
            filter={filterBy('disposition', 'phone_tree_no_connection')}
            value={stats.phoneTreeNoConnection}
            icon={<IconPhoneOff className="h-5 w-5 text-purple-500" />}
            iconBgColor="bg-purple-500/10"
            valueColor="text-purple-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.phoneRefusal', 'Phone Refusal')}
            filter={filterBy('disposition', 'phone_refusal')}
            value={stats.phoneRefusal}
            icon={<IconPhoneX className="h-5 w-5 text-yellow-500" />}
            iconBgColor="bg-yellow-500/10"
            valueColor="text-yellow-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.refuseTransfer', 'Refuse Transfer')}
            filter={filterBy('disposition', 'refuse_transfer')}
            value={stats.refuseTransfer}
            icon={<IconTransfer className="h-5 w-5 text-orange-500" />}
            iconBgColor="bg-orange-500/10"
            valueColor="text-orange-500"
            onSelected={onClickStat}
          />
          <StatCard
            label={t('dashboard.statsCards.badNumberBusySignal', 'Bad Number / Busy')}
            filter={filterBy('disposition', 'bad_number_busy_signal')}
            value={stats.badNumberBusySignal}
            icon={<IconPhoneOff className="h-5 w-5 text-red-500" />}
            iconBgColor="bg-red-500/10"
            valueColor="text-red-500"
            onSelected={onClickStat}
          />
          {/*
            Calls the SDR never classified. These used to be dropped from the
            breakdown entirely, which is why the disposition counts never added
            up to the session's total. Showing them makes the gap visible — and
            it is a process signal worth seeing.
          */}
          <StatCard
            label={t('dashboard.statsCards.unclassified', 'Unclassified')}
            filter={filterBy('disposition', 'default_action')}
            value={stats.unclassified}
            icon={<IconHelpCircle className="h-5 w-5 text-slate-400" />}
            iconBgColor="bg-slate-400/10"
            valueColor="text-slate-400"
            onSelected={onClickStat}
          />
        </div>
      </TabsContent>

      {/* Attempts (aggregate figures — not per-disposition filters) */}
      <TabsContent value="attempts">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/*
            Total Calls counts calls, not people: a contact dialled twice
            counts twice, which is why it can exceed the number of rows in the
            table below. Unique Contacts is shown next to it so the difference
            is self-explanatory rather than looking like a bug.
          */}
          <StatCard
            label={t('dashboard.statsCards.totalCalls', 'Total Calls Made')}
            value={stats.totalCalls}
            icon={<IconPhoneCall className="h-5 w-5 text-blue-500" />}
            iconBgColor="bg-blue-500/10"
            onSelected={onClickStat}
            qaId="stat-card-total-calls"
          />
          <StatCard
            label={t('dashboard.statsCards.uniqueContacts', 'Unique Contacts')}
            value={stats.uniqueContacts}
            icon={<IconUsers className="h-5 w-5 text-indigo-500" />}
            iconBgColor="bg-indigo-500/10"
            onSelected={onClickStat}
            qaId="stat-card-unique-contacts"
          />
          <StatCard
            label={t('dashboard.statsCards.dialToConnect', 'Dial-to-Connect')}
            value={`${stats.dialToConnectRatio.toFixed(1)}%`}
            icon={<IconTransfer className="h-5 w-5 text-green-500" />}
            iconBgColor="bg-green-500/10"
            valueColor="text-green-500"
            onSelected={onClickStat}
            qaId="stat-card-dial-to-connect"
          />
          <StatCard
            label={t('dashboard.statsCards.fuegoLeads')}
            value={fuegoLeadCount ?? 0}
            icon={<Flame className="h-5 w-5 text-amber-500" />}
            iconBgColor="bg-amber-500/10"
            valueColor="text-amber-500"
            onSelected={onClickStat}
            qaId="stat-card-fuego-leads"
          />
        </div>
      </TabsContent>
    </Tabs>
  );
}
